<template>
  <div class="knowledge-link bg-neutral-50 min-h-screen">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <!-- Hero -->
      <section class="text-center mb-8">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 text-sky-600 text-xs font-medium mb-4"
        >
          <i class="fa fa-link"></i>
          学科知识点体系
        </div>
        <h1 class="text-3xl md:text-5xl font-bold text-neutral-900 mb-4">知识链接</h1>
        <p class="text-base md:text-lg text-neutral-500 max-w-2xl mx-auto">
          按学科把知识点串成体系：章节、知识点、要点与例题，随时补充、随时查阅。
        </p>
      </section>

      <!-- 学段 -->
      <section class="mb-4">
        <div class="flex gap-2">
          <button
            v-for="st in stages"
            :key="st.id"
            @click="switchStage(st.id)"
            class="px-3.5 py-1.5 rounded-full text-xs transition-colors"
            :class="
              stage === st.id
                ? 'bg-neutral-900 text-white'
                : 'bg-white border border-neutral-200 text-neutral-600 hover:border-neutral-400'
            "
          >
            {{ st.label }}
          </button>
        </div>
      </section>

      <!-- 学科 + 操作 -->
      <section class="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div class="flex gap-2 overflow-x-auto pb-1">
          <button
            v-for="s in subjects"
            :key="s"
            @click="switchSubject(s)"
            class="px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors"
            :class="
              subject === s
                ? 'bg-primary text-white font-medium'
                : 'bg-white text-neutral-600 border border-neutral-200 hover:border-primary hover:text-primary'
            "
          >
            {{ s }}
          </button>
        </div>
        <button
          @click="openCreate(null)"
          class="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90"
        >
          <i class="fa fa-plus mr-1"></i> 新增章节
        </button>
      </section>

      <!-- 加载 -->
      <div v-if="loading" class="text-center py-16 text-neutral-400">
        <i class="fa fa-circle-o-notch fa-spin text-2xl"></i>
      </div>

      <!-- 空态 -->
      <div
        v-else-if="!tree.length"
        class="bg-white rounded-xl border border-dashed border-neutral-300 py-16 text-center"
      >
        <i class="fa fa-sitemap text-3xl text-neutral-300 mb-3"></i>
        <p class="text-neutral-500 text-sm">{{ subject }}还没有知识点体系</p>
        <button
          @click="openCreate(null)"
          class="mt-4 px-4 py-2 rounded-lg bg-primary text-white text-sm"
        >
          从第一个章节开始
        </button>
      </div>

      <!-- 知识树 -->
      <div v-else class="space-y-4">
        <div
          v-for="root in tree"
          :key="root.id"
          class="bg-white rounded-xl border border-neutral-200 overflow-hidden"
        >
          <!-- 章节行 -->
          <div
            class="flex items-center gap-3 p-4 cursor-pointer hover:bg-neutral-50"
            @click="toggle(root.id)"
          >
            <i
              class="fa text-neutral-400 text-xs transition-transform"
              :class="expanded[root.id] ? 'fa-chevron-down' : 'fa-chevron-right'"
            ></i>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <p class="font-bold text-neutral-900 truncate">{{ root.title }}</p>
                <span
                  v-if="root.isSystem"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-400 flex-shrink-0"
                  >内置</span
                >
              </div>
              <p class="text-xs text-neutral-400 mt-0.5">
                {{ (root.children || []).length }} 个知识点
              </p>
            </div>
            <div class="flex items-center gap-1.5" @click.stop>
              <button
                class="px-2 py-1 rounded text-xs text-neutral-500 hover:text-primary hover:bg-neutral-100"
                title="添加知识点"
                @click="openCreate(root)"
              >
                <i class="fa fa-plus"></i>
              </button>
              <template v-if="!root.isSystem">
                <button
                  class="px-2 py-1 rounded text-xs text-neutral-500 hover:text-primary hover:bg-neutral-100"
                  title="编辑章节"
                  @click="openEdit(root)"
                >
                  <i class="fa fa-pencil"></i>
                </button>
                <button
                  class="px-2 py-1 rounded text-xs text-neutral-400 hover:text-red-500 hover:bg-neutral-100"
                  title="删除章节"
                  @click="remove(root)"
                >
                  <i class="fa fa-trash-o"></i>
                </button>
              </template>
            </div>
          </div>

          <!-- 知识点列表 -->
          <div v-if="expanded[root.id]" class="border-t border-neutral-100">
            <div
              v-if="!(root.children || []).length"
              class="px-4 py-4 text-xs text-neutral-400"
            >
              还没有知识点，
              <button class="text-primary hover:underline" @click="openCreate(root)">
                添加一个
              </button>
            </div>
            <div
              v-for="leaf in root.children"
              :key="leaf.id"
              class="flex items-center gap-3 px-4 py-3 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 group"
            >
              <i class="fa fa-circle text-[5px] text-sky-400 ml-2"></i>
              <div class="flex-1 min-w-0 cursor-pointer" @click="openView(leaf)">
                <p class="text-sm text-neutral-800 truncate">{{ leaf.title }}</p>
                <p
                  v-if="leaf.content"
                  class="text-xs text-neutral-400 truncate mt-0.5"
                >
                  {{ plain(leaf.content) }}
                </p>
              </div>
              <span
                v-if="leaf.isSystem"
                class="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-400 flex-shrink-0"
                >内置</span
              >
              <div
                v-else
                class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <button
                  class="px-2 py-1 rounded text-xs text-neutral-500 hover:text-primary"
                  @click="openEdit(leaf)"
                >
                  <i class="fa fa-pencil"></i>
                </button>
                <button
                  class="px-2 py-1 rounded text-xs text-neutral-400 hover:text-red-500"
                  @click="remove(leaf)"
                >
                  <i class="fa fa-trash-o"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 查看 / 编辑 -->
    <div
      v-if="visible"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4"
    >
      <div class="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold text-neutral-900">
            {{ editing ? (form.id ? "编辑知识点" : "新增") : "知识点详情" }}
          </h2>
          <button class="text-neutral-400 hover:text-neutral-600" @click="close">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- 阅读态 -->
        <div v-if="!editing">
          <p class="text-xs text-neutral-400 mb-1">
            {{ detail.subject }}<span v-if="detail.parentTitle"> · {{ detail.parentTitle }}</span>
          </p>
          <h3 class="text-xl font-bold text-neutral-900 mb-3">{{ detail.title }}</h3>
          <div
            class="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap min-h-[80px]"
          >
            {{ detail.content || "还没有填写内容" }}
          </div>
          <div class="flex justify-end gap-2 mt-6">
            <button
              class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600"
              @click="close"
            >
              关闭
            </button>
            <button
              class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium"
              @click="startEdit(detail)"
            >
              编辑
            </button>
          </div>
        </div>

        <!-- 编辑态 -->
        <div v-else class="space-y-4">
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">标题 *</label>
            <input
              v-model="form.title"
              maxlength="100"
              placeholder="如：分数乘整数"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">
              内容（要点 / 易错点 / 例题）
            </label>
            <textarea
              v-model="form.content"
              rows="10"
              placeholder="写下这个知识点的关键内容…"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-y"
            ></textarea>
          </div>
          <p class="text-[11px] text-neutral-400">
            归属：{{ form.subject }}<span v-if="form.parentTitle"> · {{ form.parentTitle }}</span>
          </p>

          <div class="flex justify-end gap-2 pt-2">
            <button
              class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600"
              @click="close"
            >
              取消
            </button>
            <button
              class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50"
              :disabled="saving"
              @click="save"
            >
              {{ saving ? "保存中..." : "保存" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { requireLoginPage, isLogin } from "@/utils/authGuard";

export default {
  name: "KnowledgeLinkPage",
  data() {
    return {
      loading: false,
      saving: false,
      stages: [
        { id: "primary", label: "小学" },
        { id: "junior", label: "初中" },
        { id: "senior", label: "高中" },
      ],
      stage: "primary",
      subjects: ["语文", "数学", "英语", "物理", "化学", "生物", "政治", "历史", "地理", "科学"],
      subject: "语文",
      flat: [],
      expanded: {},

      visible: false,
      editing: false,
      detail: {},
      form: this.emptyForm(),
    };
  },
  computed: {
    tree() {
      const roots = this.flat.filter((x) => !x.parentId);
      const byParent = {};
      this.flat.forEach((x) => {
        if (x.parentId) {
          (byParent[x.parentId] = byParent[x.parentId] || []).push(x);
        }
      });
      return roots.map((r) => ({ ...r, children: byParent[r.id] || [] }));
    },
  },
  mounted() {
    if (!requireLoginPage(this, "知识链接")) return;
    this.load();
  },
  methods: {
    emptyForm() {
      return {
        id: "",
        subject: "语文",
        parentId: "",
        parentTitle: "",
        title: "",
        content: "",
      };
    },

    plain(text) {
      return (text || "").replace(/\s+/g, " ").slice(0, 60);
    },

    async load() {
      if (!isLogin()) return;
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "knowledge-link/list",
          method: "GET",
          params: { subject: this.subject, stage: this.stage },
        });
        if (res.code === 200 && res.data) {
          this.flat = res.data.list || [];
          // 默认展开第一个章节
          if (this.flat.length) {
            const firstRoot = this.flat.find((x) => !x.parentId);
            if (firstRoot) this.expanded[firstRoot.id] = true;
          }
        } else {
          ElMessage.error(res.msg || "加载失败");
        }
      } catch (e) {
        ElMessage.error("加载失败，请稍后重试");
      }
      this.loading = false;
    },

    switchStage(s) {
      this.stage = s;
      this.flat = [];
      this.expanded = {};
      this.load();
    },

    switchSubject(s) {
      this.subject = s;
      this.flat = [];
      this.expanded = {};
      this.load();
    },

    toggle(id) {
      this.expanded[id] = !this.expanded[id];
    },

    openView(leaf) {
      const parent = this.flat.find((x) => x.id === leaf.parentId);
      this.detail = { ...leaf, parentTitle: parent ? parent.title : "" };
      this.editing = false;
      this.visible = true;
    },

    openCreate(parent) {
      if (!requireLoginPage(this, "维护知识点")) return;
      this.form = this.emptyForm();
      this.form.subject = this.subject;
      if (parent) {
        this.form.parentId = parent.id;
        this.form.parentTitle = parent.title;
      }
      this.editing = true;
      this.visible = true;
    },

    openEdit(node) {
      this.startEdit(node);
    },

    startEdit(node) {
      const parent = this.flat.find((x) => x.id === node.parentId);
      this.form = {
        id: node.id || "",
        subject: node.subject || this.subject,
        parentId: node.parentId || "",
        parentTitle: parent ? parent.title : "",
        title: node.title || "",
        content: node.content || "",
      };
      this.editing = true;
      this.visible = true;
    },

    close() {
      this.visible = false;
      this.editing = false;
    },

    async save() {
      if (!this.form.title.trim()) {
        ElMessage.warning("请填写标题");
        return;
      }
      this.saving = true;
      try {
        const payload = {
          id: this.form.id || null,
          subject: this.form.subject,
          parentId: this.form.parentId,
          title: this.form.title,
          content: this.form.content,
          stage: this.stage,
        };
        const res = await this.$axios({
          url: "knowledge-link/save",
          method: "POST",
          data: { data: await this.$aesEncrypt(payload) },
        });
        if (res.code === 200) {
          ElMessage.success("已保存");
          if (this.form.parentId) {
            this.expanded[this.form.parentId] = true;
          }
          this.close();
          await this.load();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请稍后重试");
      }
      this.saving = false;
    },

    async remove(node) {
      const isRoot = !node.parentId;
      try {
        await ElMessageBox.confirm(
          isRoot
            ? `删除「${node.title}」会连同其下所有知识点一起删除，确定？`
            : `确定删除知识点「${node.title}」？`,
          "删除",
          { confirmButtonText: "删除", cancelButtonText: "取消", type: "warning" }
        );
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "knowledge-link/remove",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: node.id }) },
        });
        if (res.code === 200) {
          ElMessage.success("已删除");
          await this.load();
        } else {
          ElMessage.error(res.msg || "删除失败");
        }
      } catch (e) {
        ElMessage.error("删除失败");
      }
    },
  },
};
</script>
